import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FileText, Eye, Trash2, Phone, Mail, MapPin, Calendar,
  Check, Clock, MessageSquare, Search, Filter
} from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700' },
  contacted: { label: 'Contacté', color: 'bg-blue-100 text-blue-700' },
  quoted: { label: 'Devis envoyé', color: 'bg-purple-100 text-purple-700' },
  completed: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700' }
};

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, pending: 0, unread: 0 });
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchQuotes();
    fetchStats();
  }, []);

  const fetchQuotes = async () => {
    try {
      const token = localStorage.getItem('jardily_token');
      const res = await axios.get(`${API}/quote-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(res.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('jardily_token');
      const res = await axios.get(`${API}/quote-requests/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const openDetail = async (quote) => {
    setSelectedQuote(quote);
    setNotes(quote.notes || '');
    setIsDetailOpen(true);

    // Mark as read
    if (!quote.is_read) {
      const token = localStorage.getItem('jardily_token');
      await axios.put(`${API}/quote-requests/${quote.id}`, { is_read: true }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchQuotes();
      fetchStats();
    }
  };

  const updateStatus = async (status) => {
    if (!selectedQuote) return;
    const token = localStorage.getItem('jardily_token');
    try {
      await axios.put(`${API}/quote-requests/${selectedQuote.id}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Statut mis à jour');
      fetchQuotes();
      setSelectedQuote({ ...selectedQuote, status });
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const saveNotes = async () => {
    if (!selectedQuote) return;
    const token = localStorage.getItem('jardily_token');
    try {
      await axios.put(`${API}/quote-requests/${selectedQuote.id}`, { notes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notes sauvegardées');
      fetchQuotes();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    const token = localStorage.getItem('jardily_token');
    try {
      await axios.delete(`${API}/quote-requests/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Demande supprimée');
      fetchQuotes();
      fetchStats();
      setIsDetailOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredQuotes = quotes.filter(q => {
    const matchesSearch = q.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         q.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         q.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || q.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-2xl text-stone-900">Demandes de devis</h1>
          <p className="text-stone-600 text-sm mt-1">
            Gérez les demandes de devis pour les services sur mesure
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-stone-600 text-sm">Total demandes</span>
            </div>
            <p className="font-heading font-bold text-3xl text-stone-900">{stats.total}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-stone-600 text-sm">En attente</span>
            </div>
            <p className="font-heading font-bold text-3xl text-stone-900">{stats.pending}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-red-600" />
              </div>
              <span className="text-stone-600 text-sm">Non lues</span>
            </div>
            <p className="font-heading font-bold text-3xl text-stone-900">{stats.unread}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher..."
              className="pl-10 rounded-xl"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="contacted">Contacté</SelectItem>
              <SelectItem value="quoted">Devis envoyé</SelectItem>
              <SelectItem value="completed">Terminé</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Quotes List */}
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-600 border-t-transparent"></div>
            </div>
          ) : filteredQuotes.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-600">Aucune demande de devis</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-100">
              {filteredQuotes.map((quote) => (
                <div 
                  key={quote.id}
                  className={`p-4 hover:bg-stone-50 cursor-pointer transition-colors ${!quote.is_read ? 'bg-emerald-50/50' : ''}`}
                  onClick={() => openDetail(quote)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        {!quote.is_read && (
                          <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                        )}
                        <span className="font-semibold text-stone-900">{quote.name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusLabels[quote.status]?.color || 'bg-stone-100'}`}>
                          {statusLabels[quote.status]?.label || quote.status}
                        </span>
                      </div>
                      <p className="text-emerald-600 font-medium text-sm mb-1">{quote.service_name}</p>
                      <div className="flex items-center gap-4 text-stone-500 text-sm">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" />
                          {quote.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" />
                          {quote.phone}
                        </span>
                      </div>
                    </div>
                    <div className="text-stone-400 text-xs">
                      {formatDate(quote.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Détails de la demande</DialogTitle>
          </DialogHeader>
          
          {selectedQuote && (
            <div className="space-y-4">
              {/* Service */}
              <div className="bg-emerald-50 rounded-xl p-4">
                <p className="text-sm text-emerald-600 font-medium">Service demandé</p>
                <p className="font-semibold text-stone-900">{selectedQuote.service_name}</p>
                <p className="text-xs text-stone-500 mt-1">
                  Catégorie: {selectedQuote.service_category === 'conseil' ? 'Conseil & Conception' : 'Aménagements & Installations'}
                </p>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-stone-500">Nom</p>
                  <p className="font-medium">{selectedQuote.name}</p>
                </div>
                <div>
                  <p className="text-sm text-stone-500">Téléphone</p>
                  <a href={`tel:${selectedQuote.phone}`} className="font-medium text-emerald-600 hover:underline">
                    {selectedQuote.phone}
                  </a>
                </div>
                <div>
                  <p className="text-sm text-stone-500">Email</p>
                  <a href={`mailto:${selectedQuote.email}`} className="font-medium text-emerald-600 hover:underline">
                    {selectedQuote.email}
                  </a>
                </div>
                <div>
                  <p className="text-sm text-stone-500">Date</p>
                  <p className="font-medium">{formatDate(selectedQuote.created_at)}</p>
                </div>
              </div>

              {/* Address */}
              {(selectedQuote.address || selectedQuote.city) && (
                <div>
                  <p className="text-sm text-stone-500">Adresse</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-stone-400" />
                    {selectedQuote.address} {selectedQuote.city}
                  </p>
                </div>
              )}

              {/* Message */}
              {selectedQuote.message && (
                <div>
                  <p className="text-sm text-stone-500">Description du projet</p>
                  <p className="text-stone-700 bg-stone-50 rounded-lg p-3 text-sm">
                    {selectedQuote.message}
                  </p>
                </div>
              )}

              {/* Status */}
              <div>
                <p className="text-sm text-stone-500 mb-2">Statut</p>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(statusLabels).map(([key, { label, color }]) => (
                    <button
                      key={key}
                      onClick={() => updateStatus(key)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        selectedQuote.status === key 
                          ? color + ' ring-2 ring-offset-1 ring-current' 
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <p className="text-sm text-stone-500 mb-2">Notes internes</p>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ajouter des notes..."
                  rows={3}
                />
                <Button 
                  onClick={saveNotes} 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                >
                  Sauvegarder les notes
                </Button>
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => deleteQuote(selectedQuote.id)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
                <div className="flex gap-2">
                  <a href={`tel:${selectedQuote.phone}`}>
                    <Button variant="outline">
                      <Phone className="w-4 h-4 mr-2" />
                      Appeler
                    </Button>
                  </a>
                  <a href={`mailto:${selectedQuote.email}`}>
                    <Button className="btn-primary">
                      <Mail className="w-4 h-4 mr-2" />
                      Envoyer un email
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminQuotes;
